import React, { useCallback, useEffect } from 'react';
import { BulkOperationsToolbarSkeleton } from '@atlassian/jira-bulk-operations-core/src/ui/toolbar/bulk-operations-toolbar-skeleton/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import type { AnyEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import type { EntryPointReferenceSubject } from '@atlassian/jira-entry-point/src/controllers/utils/use-entry-point-load-manager/index.tsx';
import { ToolbarWrapper } from '@atlassian/jira-floating-toolbar/src/ui/toolbar-wrapper/index.tsx';
import { useBulkOperationsActions } from '@atlassian/jira-bulk-operations-common/src/controllers/use-bulk-operations/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { asyncBulkOpsCoreEntryPoint } from '../entrypoint.tsx';
import type { BulkOpsCoreType } from './ui/index.tsx';
import { useBulkopsCoreEntryPoint } from './utils.tsx';

// Will be removed when cleaning up `bulk_delete_experiment`
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
const entryPointParams = {} as any;
export const AsyncBulkOpsCore = <TEntryPoint extends AnyEntryPoint | undefined = undefined>(
	props: BulkOpsCoreType<TEntryPoint>,
) => {
	const { entryPointActions, entryPointReferenceSubject } = expVal(
		'bulk_delete_experiment',
		'isEnabled',
		false,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBulkopsCoreEntryPoint()
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useEntryPoint(asyncBulkOpsCoreEntryPoint, entryPointParams);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		entryPointActions.load();
	}, [entryPointActions]);

	return (
		<JiraEntryPointContainer
			id="jira.backlog.bulk.operations.core"
			packageName="jiraBulkOperationsCore"
			entryPointReferenceSubject={entryPointReferenceSubject}
			fallback={<></>}
			runtimeProps={props}
		/>
	);
};

type BulkOpsCoreWithToolbarProps<TEntryPoint extends AnyEntryPoint | undefined = undefined> =
	BulkOpsCoreType<TEntryPoint> & {
		bulkOpsCoreEntryPointReference?: // remove ? on bulk_operations_floating_toolbar cleanup
		EntryPointReferenceSubject<typeof asyncBulkOpsCoreEntryPoint>;
	};

export const BulkOpsCoreWithToolbar = <TEntryPoint extends AnyEntryPoint | undefined = undefined>({
	bulkOpsCoreEntryPointReference,
	...props
}: BulkOpsCoreWithToolbarProps<TEntryPoint>) => {
	const hasSelectAllButton = Boolean(props.onSelectAll);
	const extraToolbarButtonsSkeleton =
		props.extraToolbarButtons?.map((button) => ({
			hasIcon: Boolean(button.icon),
			text: button.name,
		})) || [];

	const { dismissBulkOperation } = fg('bulk_operations_analytics_consolidation')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBulkOperationsActions()
		: { dismissBulkOperation: undefined };
	const { createAnalyticsEvent } = fg('bulk_operations_analytics_consolidation')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useAnalyticsEvents()
		: { createAnalyticsEvent: undefined };

	const onDeselectAll = useCallback(() => {
		// TODO: remove this if statement when cleaning up bulk_operations_analytics_consolidation'
		if (createAnalyticsEvent && dismissBulkOperation) {
			dismissBulkOperation({
				createAnalyticsEvent,
				dismissType: 'DESELECT_ALL_ISSUES',
			});
		}
	}, [createAnalyticsEvent, dismissBulkOperation]);

	return (
		<ToolbarWrapper
			show={props.selectedIssueIds.length > 0}
			width="calc(100% - var(--leftSidebarWidth, 0px))"
			{...(fg('bulk_operations_analytics_consolidation') && { onDeselectAll })}
		>
			{bulkOpsCoreEntryPointReference && (
				<JiraEntryPointContainer
					id="jiraAsyncBulkOperationsCoreWithToolBar"
					packageName="jiraBulkOperationsCoreWithToolBar"
					teamName="deliveroo"
					entryPointReferenceSubject={bulkOpsCoreEntryPointReference}
					fallback={
						<BulkOperationsToolbarSkeleton
							buttons={extraToolbarButtonsSkeleton}
							hasSelectAllButton={hasSelectAllButton}
						/>
					}
					runtimeProps={props}
				/>
			)}
		</ToolbarWrapper>
	);
};
